import React from "react";
import Fragment from "graphql-fragments";
import gql from "graphql-tag";
import { injectIntl, defineMessages } from "react-intl";
import { compose, setStatic, withState } from "recompose";

import trackEvent from "theme/modules/Analytics/trackEvent";
import { ErrorAlert } from "theme/components/molecules/Alert";
import NumberInput from "./NumberInput";
import Form from "theme/components/atoms/Form/Form";
import FormItem from "theme/components/molecules/Form/Item";
import { PrimaryButton } from "theme/components/atoms/Button";

import EnhanceAddToCart from "./EnhanceAddToCart";
import AddToCartMutation from "./AddToCartMutation.gql";
import { trackAddToCart } from "../../components/helpers/AnalyticsV4/TrackAnlyticsV4";
import { withShop } from "web/core/shop/ShopContext";

export const AddToCart = ({
  intl,
  addToCart,
  commandPending,
  label,
  message,
  product,
  unavailableLabel,
  qty,
  selectedOptions,
  stock,
  selectedCustomOptions,
  displayMaxQtyMessage,
  setDisplayMaxQtyMessage,
}) => {
  let maxQty = 9999;
  if (stock && stock.qty) {
    maxQty = stock.qty;
  }
  const messages = defineMessages({
    qty: {
      id: "modules.AddToCart.qty",
      defaultMessage: "Quantity",
    },
    btnWarningTitle: {
      id: "modules.AddToCart.missingOptions",
      defaultMessage: "Please select your options",
    },
    stockReajust: {
      id: "modules.AddToCart.stockReajust",
      defaultMessage:
        "The quantity has been adjusted according to the remaining stock.",
    },
  });

  let allOptionsAreSet = true;
  if (product.options) {
    allOptionsAreSet = product.options.every(
      (option) => selectedOptions[option.id]
    );
  }

  if (product.customOptions) {
    allOptionsAreSet =
      allOptionsAreSet &&
      product.customOptions.every(
        (option) =>
          selectedCustomOptions[option.option_id] || !option.is_required
      );
  }

  return product.stock.isInStock && product.currentConfigIsInStock !== false ? (
    <Form
      className="add-to-cart"
      onValidSubmit={({ qty_input }) =>
        addToCart({ qty: typeof qty === "number" ? qty : qty_input })
      }
    >
      {message && (
        <div className="add-to-cart__error">
          <ErrorAlert>{message}</ErrorAlert>
        </div>
      )}
      {typeof qty !== "number" && (
        <div className="add-to-cart__qty">
          <FormItem
            label={intl.formatMessage(messages.qty)}
            hideOptionalLabel={true}
            inline
          >
            <NumberInput
              name="qty_input"
              id="qty_input"
              value={1}
              disabled={commandPending}
              maxValue={maxQty}
              setDisplayMaxQtyMessage={setDisplayMaxQtyMessage}
            />
          </FormItem>
        </div>
      )}
      {displayMaxQtyMessage && (
        <ErrorAlert>{intl.formatMessage(messages.stockReajust)}</ErrorAlert>
      )}

      <div className="add-to-cart__button">
        <PrimaryButton
          state={
            commandPending
              ? "pending"
              : !allOptionsAreSet
              ? "disabled"
              : undefined
          }
          type="submit"
          disabled={!allOptionsAreSet}
          title={
            allOptionsAreSet ? "" : intl.formatMessage(messages.btnWarningTitle)
          }
        >
          {label}
        </PrimaryButton>
      </div>
      <link itemProp="availability" href="http://schema.org/InStock" />
    </Form>
  ) : (
    <p className="product__out-of-stock">
      {message && <ErrorAlert>{message}</ErrorAlert>}
      {unavailableLabel}
      <link itemProp="availability" href="http://schema.org/OutOfStock" />
    </p>
  );
};

export default compose(
  injectIntl,
  setStatic("fragments", {
    stock: new Fragment(
      gql`
        fragment Stock on ProductType {
          sku
          stock {
            IsInStock
          }
        }
      `
    ),
  }),
  withShop(),
  EnhanceAddToCart({
    AddToCartMutation,
    addToCartCallback:
      ({ onProductAdded, shop, product }, variables) =>
      ({ status, data }) => {
        if (status === "success") {
          trackEvent("Product Added", {
            sku: product.sku,
            price: product.price,
          });
          trackAddToCart(shop, product, variables.qty);
          onProductAdded();
        }
      },
  }),
  withState("displayMaxQtyMessage", "setDisplayMaxQtyMessage", false)
)(AddToCart);
